import React, { useState, useRef, useEffect } from "react";

import {
  Authenticator,
  Text,
  useTheme,
  useAuthenticator,
  PhoneNumberField,
  TextField,
} from "@aws-amplify/ui-react";
import { Auth, I18n } from "aws-amplify";
import "./login.scss";
import Oxygen7Logo from "../../assets/img/oxygen7-logo-stacked-white.png";
import BackgroundImg from "../../assets/img/oxygen7-logomark-white.png";
import Oxygen7ColorLogo from "../../assets/img/oxygen7-logo.png";
import {
  Button,
  Classes,
  Dialog,
  FormGroup,
  Icon,
  InputGroup,
} from "@blueprintjs/core";
import { alertStore } from "../../stores";
import { history } from "../../utils/history-holder";
import { SIGNUP_REDIRECT_SEARCH } from "../../constants";
import { appRedirect } from "../../utils/app-redirect";

import {
  shortLivedConfig,
  longLivedConfig,
} from "../../constants/aws-auth-config";
import { createMerchantUser } from "../../services/auth";

let resolveFunction;
const pending = new Promise((resolve) => {
  resolveFunction = resolve;
});

I18n.putVocabularies({
  en: {
    "2 validation errors detected: Value at 'userAlias' failed to satisfy constraint: Member must satisfy regular expression pattern: [\\p{L}\\p{M}\\p{S}\\p{N}\\p{P}]+; Value at 'userName' failed to satisfy constraint: Member must satisfy regular expression pattern: [\\p{L}\\p{M}\\p{S}\\p{N}\\p{P}]+":
      "Incorrect username or password.",
  },
});

const SignInFooter = () => {
  const { toSignUp, toResetPassword } = useAuthenticator();

  return (
    <div className="signin-footer">
      <div className="forgot-password">
        <span onClick={() => toResetPassword()} style={{ cursor: "pointer" }}>
          Forgot password?
        </span>
      </div>
      <div className="signup-link">
        Don’t have an account?{" "}
        <span className="text-button" onClick={() => toSignUp()}>
          Sign Up here
        </span>
      </div>
    </div>
  );
};

const SignUpFooter = () => {
  const { toSignIn } = useAuthenticator();

  return (
    <div className="signin-footer">
      <div className="signup-link">
        Already have an Account?{" "}
        <span className="text-button" onClick={() => toSignIn()}>
          Sign In here
        </span>
      </div>
    </div>
  );
};

export default function OtpInputs({ length = 6, onChange, submitCode }) {
  const inputs = useRef([]);

  const handleChange = (e, idx) => {
    let value = e.target.value.replace(/\D/g, "");

    if (value) {
      e.target.value = value;

      if (idx < length - 1) {
        inputs.current[idx + 1].focus();
      }

      handleUpdateCode();
    } else {
      e.target.value = "";
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !e.target.value && idx > 0) {
      inputs.current[idx - 1].focus();
    }
    setTimeout(() => {
      handleUpdateCode();
    }, 0);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").slice(0, length);
    paste.split("").forEach((char, i) => {
      inputs.current[i].value = char.toUpperCase();
    });
    inputs.current[Math.min(paste.length, length - 1)].focus();
    handleUpdateCode();
  };

  const handleUpdateCode = () => {
    const code = inputs.current.map((input) => input.value).join("");
    onChange && onChange(code);
  };

  const handleSubmitCode = () => {
    const code = inputs.current.map((input) => input.value).join("");
    if (code.length < 6) {
      return;
    }

    submitCode();
  };

  return (
    <div className="otp-inputs" onPaste={handlePaste}>
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          type="text"
          maxLength={1}
          ref={(el) => (inputs.current[idx] = el)}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => {
            handleKeyDown(e, idx);
            if (e.key === "Enter") {
              e.preventDefault();
              handleSubmitCode();
            }
          }}
        />
      ))}
    </div>
  );
}

const CustomSignUpHeader = ({ username, signUpFormData }) => {
  const [code, setCode] = useState();
  const { toSignIn } = useAuthenticator();
  const [counter, setCounter] = useState(0);
  const [loading, setLoading] = useState(false);

  const confirmSignUp = async (username, code) => {
    try {
      setLoading(true);
      await Auth.confirmSignUp(username, code);
      if (signUpFormData) {
        const newUser = {
          email: signUpFormData.email || '',
          name: signUpFormData.name || '',
          contact_number: signUpFormData.contact_number || ''
        }
        try {
          await createMerchantUser(newUser)
        } catch(e) {
        } finally {
          setLoading(false);
        }
      }
      alertStore.add({ text: "Sign Up successfully" });
      toSignIn();
    } catch (e) {
      alertStore.add({ text: e.message });
    }
  };

  useEffect(() => {
    let timer;
    if (counter > 0) {
      timer = setTimeout(() => setCounter(counter - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [counter]);

  const resendCode = async () => {
    if (counter > 0) return;
    try {
      await Auth.resendSignUp(username);
      alertStore.add({ text: "Resend code successfully" });
      setCounter(60);
    } catch (e) {
      alertStore.add({ text: e.message });
    }
  };

  return (
    <div className="otp-container">
      <div className="box-action d-flex">
        <img
          className="back-button"
          src={'/img/icon-back.png'}
          width={24}
          alt="Back"
          onClick={() => toSignIn()}
        />
        <div className="verify-email">
          <img src={'/img/sms.png'} width={22} alt="Icon Sms" />
          Verify Email
        </div>
      </div>
      <div className="box-action">
        <h2 className="otp-title">Check Your Email</h2>
        <p>
          We sent a code to <span className="email-text">{username}</span>
        </p>
      </div>
      <OtpInputs
        length={6}
        onChange={(code) => {
          setCode(code);
        }}
        submitCode={() => confirmSignUp(username, code)}
      />
      <div className="resend-container">
        <span>Didn’t get a code?</span>
        <button
          className="resend-button"
          disabled={counter > 0}
          onClick={() => resendCode()}
        >
          {counter > 0 ? `Resend after ${counter}s` : "Resend Code"}
        </button>
      </div>
      <Button
        loading={loading}
        className="verify-button"
        fill
        text="Verify"
        intent="primary"
        onClick={() => confirmSignUp(username, code)}
      />
    </div>
  );
};

const CustomHeader = ({ username }) => {
  const { route } = useAuthenticator((context) => [context.route]);
  const [code, setCode] = useState();
  const { toSignUp, toResetPassword, toSignIn } = useAuthenticator();
  const [stateConfirmResetPassword, setStateConfirmResetPassword] =
    useState("confirm-code");
  const [counter, setCounter] = useState(0);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  let res = null;
  const submitCode = async () => {
    if (!code || code.length < 6) return;
    try {
      await Auth.forgotPasswordSubmit(username, code, "P@sswrd");
      setStateConfirmResetPassword("confirm-password");
    } catch (e) {
      if (e.message.includes("Invalid verification code") || e.message.includes("Attempt limit exceeded")) {
        return alertStore.add({ text: e.message });
      }
      if (e.message.includes("Password does not conform to policy")) {
        setStateConfirmResetPassword("confirm-password");
      }
    }
  };

  useEffect(() => {
    let timer;
    if (counter > 0) {
      timer = setTimeout(() => setCounter(counter - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [counter]);

  const onSubmitChangePassword = async () => {
    if (password != confirmPassword) {
      return alertStore.add({ text: "Passwords do not match" });
    }

    try {
      await Auth.forgotPasswordSubmit(username, code, password);
      toSignIn();
    } catch (e) {
      if (e.message.includes("Invalid verification code")) {
        setPassword("");
        setConfirmPassword("");
        setStateConfirmResetPassword("confirm-code");
      }
      alertStore.add({ text: e.message });
    }
  };

  const handleResendCode = async () => {
    try {
      const res = await Auth.forgotPassword(username);
      alertStore.add({ text: "Resend code successfully" });
      setCounter(60);
    } catch (e) {
      alertStore.add({ text: e.message });
    }
  };

  switch (route) {
    case "signIn":
      res = <h1 className="login-title">Login to your account</h1>;
      break;
    case "signUp":
      res = <h1 className="login-title">Sign Up</h1>;
      break;
    case "resetPassword":
      res = (
        <>
          <div className="box-action group-back-button box-action-forgot d-flex mb-48">
            <span
              className="btn-custom btn-back"
              tabIndex="0"
              href="/login"
              data-discover="true"
              onClick={() => toSignIn()}
            >
              <svg
                height="24px"
                width="24px"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5 8.25 12l7.5-7.5"
                ></path>
              </svg>
              <span className="MuiTouchRipple-root css-4mb1j7"></span>
            </span>
            <span
              className="btn-custom btn-text"
              tabIndex="0"
              href="/login"
              data-discover="true"
            >
              <img src={'/img/lock.png'} alt="icon" />
              <span>Reset Password</span>
            </span>
          </div>
          <div className="box-action box-action-forgot">
            <h2 className="title-forgot">Forgot Password?</h2>
            <p className="subtitle">
              No worries we will send you reset instructions.
            </p>
          </div>
        </>
      );
      break;
    case "confirmResetPassword":
      res = (
        <>
          {stateConfirmResetPassword === "confirm-code" && (
            <div className="reset-password-container otp-container">
              <div className="box-action group-back-button box-action-forgot d-flex mb-48">
                <span
                  className="btn-custom btn-back"
                  tabIndex="0"
                  href="/login"
                  data-discover="true"
                  onClick={() => toSignIn()}
                >
                  <svg
                    height="24px"
                    width="24px"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 19.5 8.25 12l7.5-7.5"
                    ></path>
                  </svg>
                  <span className="MuiTouchRipple-root css-4mb1j7"></span>
                </span>
                <span
                  className="btn-custom btn-text"
                  tabIndex="0"
                  href="/login"
                  data-discover="true"
                >
                  <img src={'/img/sms2.png'} alt="icon" />
                  <span>Verify Email</span>
                </span>
              </div>
              <div className="box-action">
                <h2 className="otp-title">Check Your Email</h2>
                <p>
                  If <span className="email-text">{username}</span> is
                  registered, you’ll receive a password reset OTP.
                </p>
              </div>

              <OtpInputs
                length={6}
                onChange={(code) => {
                  setCode(code);
                }}
                submitCode={() => submitCode()}
              />

              <div className="resend-container ">
                <span>Didn’t get a code?</span>
                <button
                  className="resend-button"
                  disabled={counter > 0}
                  onClick={() => handleResendCode()}
                >
                  {counter > 0 ? `Resend after ${counter}s` : "Resend Code"}
                </button>
              </div>

              <button className="verify-button" onClick={() => submitCode()}>
                Continue
              </button>
            </div>
          )}
          {stateConfirmResetPassword === "confirm-password" && (
            <div className="reset-password-container otp-container">
              <div className="box-action group-back-button box-action-forgot d-flex mb-48">
                <span
                  className="btn-custom btn-back"
                  tabIndex="0"
                  href="/login"
                  data-discover="true"
                  onClick={() => toSignIn()}
                >
                  <svg
                    height="24px"
                    width="24px"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 19.5 8.25 12l7.5-7.5"
                    ></path>
                  </svg>
                  <span className="MuiTouchRipple-root css-4mb1j7"></span>
                </span>
                <span
                  className="btn-custom btn-text"
                  tabIndex="0"
                  href="/login"
                  data-discover="true"
                >
                  <img src={'/img/password-check.png'} alt="icon" />
                  <span>Reset Password</span>
                </span>
              </div>
              <div className="box-action">
                <h2>Set New Password</h2>
                <p className="subtitle">Must be at least 8 characters.</p>
              </div>
              <form>
                <div className="box-action">
                  <label className="label-form" htmlFor="password">
                    Password
                  </label>
                  <div className="form-group">
                    <input
                      className="input-password"
                      type="password"
                      id="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>
                <div className="box-action">
                  <label className="label-form" htmlFor="confirmPassword">
                    Confirm Password
                  </label>
                  <div className="form-group">
                    <input
                      className="input-password"
                      type="password"
                      id="confirmPassword"
                      placeholder="********"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="verify-button"
                  onClick={() => {
                    onSubmitChangePassword();
                  }}
                >
                  Change Password
                </button>
              </form>
            </div>
          )}
        </>
      );
      break;
    default:
      res = null;
      break;
  }

  return res;
};

export const Login = () => {
  const [dataUser, setDataUser] = useState();
  const [code, setCode] = useState();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authState, setAuthState] = useState("signIn");
  const [usernameInput, setUsernameInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [signUpFormData, setSignUpFormData] = useState();
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    let timer;
    if (counter > 0) {
      timer = setTimeout(() => setCounter(counter - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [counter]);

  const resendCustomChallenge = async () => {
    if (counter > 0) {
      alertStore.add({ text: `Please wait ${counter} seconds before requesting a new OTP` });
      return;
    }
    try {
      const user = await Auth.signIn(usernameInput, passwordInput);
      setDataUser(user);
      // await pending;
      alertStore.add({ text: "OTP resent" });
      setCounter(60);
    } catch (err) {
      alertStore.add({ text: "Error resending OTP" });
    }
  };

  const handleVerifyCode = async () => {
    if (!code) return alertStore.add({ text: "Please enter the code" });
    if (code && code.length < 6)
      return alertStore.add({ text: "The code must be 6 characters" });
    error && setError(false);
    setLoading(true);

    try {
      const authConfig = keepSignedIn ? longLivedConfig : shortLivedConfig;

      Auth.configure({ Auth: authConfig });
      const user = await Auth.sendCustomChallengeAnswer(dataUser, code);
      if (!user.attributes?.email) {
        return alertStore.add({ text: "Login failed" });
      }

      appRedirect("merchant-portal", true);
    } catch (err) {
      alertStore.add({ text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    try {
      const res = await Auth.forgotPassword(usernameInput);
      alertStore.add({ text: "Resend code successfully" });
    } catch (e) {
      alertStore.add({ text: e.message });
    }
  };

  return (
    <div className="login-page new-layout">
      <div className="login-left">
        <img src={Oxygen7ColorLogo} className="login-mobile-logo" alt="Oxygen7" />
        {authState != "otp" && (
          <div className="login-form-container">
            <Authenticator
              initialState={authState}
              className="amplify-form"
              socialProviders={["google"]}
              signUpAttributes={["email"]}
              loginMechanisms={["email"]}
              services={{
                handleSignIn: async ({ username, password }) => {
                  try {
                    if (!username?.trim()) {
                      throw new Error("Email is required");
                    }
                    if (!password) {
                      throw new Error("Password is required");
                    }
                    const userName = username.trim()?.toLowerCase();
                    const user = await Auth.signIn(userName, password);

                    if (user?.challengeName === "CUSTOM_CHALLENGE") {
                      setAuthState("otp");
                      setUsernameInput(userName);
                      setPasswordInput(password);
                      setDataUser(user);
                      setCounter(60); // Start 60-second timer immediately
                      await pending;
                      const currentUser = await Auth.currentSession();

                      return currentUser;
                    }
                    // appRedirect('merchant-portal', true);
                    return user;
                  } catch (error) {
                    if (
                      error.message.includes(
                        "Member must satisfy regular expression pattern"
                      )
                    ) {
                      throw new Error("Incorrect email or password.");
                    }

                    if (error.message.includes("User is not confirmed")) {
                      const userName = username.trim()?.toLowerCase();
                      setUsernameInput(userName);
                      try {
                        Auth.resendSignUp(userName);
                      } catch (e) {
                        alertStore.add({ text: e.message });
                      }
                    }
                    throw error;
                  }
                },
                handleSignUp: async (formData) => {
                  if (!formData.username?.trim()) {
                    throw new Error("Email is required");
                  }
                  const dialCode = formData.attributes["custom:dial_code"];
                  const phoneNumber = formData.attributes?.phone_number
                    ?.trim()
                    .replace("+1", dialCode);
                  try {
                    const result = await Auth.signUp({
                      username: formData.username?.trim()?.toLowerCase(),
                      password: formData.password,
                      attributes: {
                        email: formData.username?.trim()?.toLowerCase(),
                        phone_number: phoneNumber,
                        name: formData.attributes?.name?.trim(),
                      },
                    });
                    setUsernameInput(formData.username?.trim()?.toLowerCase());
                    const newUser = {
                      email: formData.username?.trim()?.toLowerCase(),
                      name: formData.attributes?.name?.trim(),
                      contact_number: phoneNumber,
                    };
                    setSignUpFormData(newUser);
                    return result;
                  } catch (error) {
                    if (error.code?.includes("UsernameExistsException")) {
                      throw new Error("Email already exists");
                    }
                    if (
                      error.message.includes(
                        "Member must satisfy regular expression pattern"
                      )
                    ) {
                      throw new Error("Please enter a valid email address");
                    }
                    throw error;
                  }
                },
                handleForgotPassword: async (username) => {
                  if (!username) {
                    throw new Error("Email cannot be empty");
                  }
                  try {
                    await Auth.forgotPassword(username.trim());
                    setUsernameInput(username.trim());
                  } catch (e) {
                    if (
                      e.message.includes("Cannot reset password for the user")
                    ) {
                      throw new Error(
                        "Cannot reset password for the user as there is no registered/verified email"
                      );
                    }
                    if (e.message.includes("Attempt limit exceede")) {
                      throw new Error(
                        "Attempt limit exceeded, please try after some time."
                      );
                    }
                    throw e;
                  }
                },
              }}
              components={{
                Header: () => <CustomHeader username={usernameInput} />,
                SignIn: {
                  Footer: () => <SignInFooter />,
                },
                SignUp: {
                  FormFields() {
                    const { tokens } = useTheme();
                    return (
                      <>
                        <TextField
                          descriptiveText=""
                          placeholder="Enter your full name"
                          label="Full Name"
                          name="name"
                          errorMessage="There is an error"
                        />
                        <PhoneNumberField
                          name="phone_number"
                          label="Phone Number"
                          defaultDialCode="+975"
                          dialCodeName="custom:dial_code"
                          dialCodeLabel="Dial Code"
                          onDialCodeChange={(e) => {}}
                        />
                        <Authenticator.SignUp.FormFields />
                      </>
                    );
                  },
                  Footer: () => <SignUpFooter />,
                },
                ConfirmSignUp: {
                  Header: () => (
                    <CustomSignUpHeader
                      signUpFormData={signUpFormData}
                      username={usernameInput}
                    />
                  ),
                },
              }}
              formFields={{
                signUp: {
                  username: {
                    display: "none", // This hides the duplicate email field
                  },
                },
                signIn: {
                  username: {
                    label: "Email",
                    placeholder: "Enter your email",
                    order: 1,
                  },
                  password: {
                    label: "Password",
                    placeholder: "*********",
                    order: 2,
                  },
                },
                resetPassword: {
                  username: {
                    label: "Email",
                    placeholder: "Enter your email",
                  },
                },
              }}
            />
          </div>
        )}
        {authState == "otp" && (
          <div className="otp-container">
            <div className="box-action d-flex">
              <img
                className="back-button"
                src={'/img/icon-back.png'}
                width={24}
                alt="Back"
                onClick={() => setAuthState("signIn")}
              />
              <div className="verify-email">
                <img src={'/img/sms.png'} width={22} alt="Icon Sms" />
                Verify Email
              </div>
            </div>
            <div className="box-action">
              <h2 className="otp-title">Check Your Email</h2>
              <p>
                We sent a code to{" "}
                <span className="email-text">{usernameInput}</span>
              </p>
            </div>
            <OtpInputs
              length={6}
              onChange={(code) => setCode(code)}
              submitCode={() => handleVerifyCode()}
            />

            <div className="resend-container">
              <span>Didn’t get a code?</span>
              <button
                className="resend-button"
                disabled={counter > 0}
                onClick={() => resendCustomChallenge()}
              >
                {counter > 0 ? `Resend after ${counter}s` : "Resend Code"}
              </button>
            </div>

            {/* <button className="verify-button" onClick={() => handleConfirmOtp}>Verify</button> */}
            <Button
              className="verify-button"
              fill
              text="Verify"
              intent="primary"
              loading={loading}
              onClick={() => handleVerifyCode()}
            />
          </div>
        )}
      </div>


       <div className="box-right">
        <div className="login-right">
          <div className="login-right-box">
            <div className="right-content">
              <img className="bg-overlay" src={BackgroundImg} alt="" aria-hidden="true" />
              <img src={Oxygen7Logo} className="logo" alt="Oxygen7" />
              <div className="features">
                <div className="feature-item">
                  <span className="feature-icon">
                    <div className="icon-group">
                      <img
                        src={'/img/trend-up.png'}
                        width={16}
                        alt="Payment Efficiency"
                      />
                    </div>
                  </span>
                  <div>
                    <div className="feature-title">Payment Efficiency</div>
                    <div className="feature-desc">
                      Boost revenue with seamless customer experiences, global
                      acquiring, and more
                    </div>
                  </div>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">
                    <div className="icon-group">
                      <svg
                        focusable="false"
                        fill="#fff"
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                      >
                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2m6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56M12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96M4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56m2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8M12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96M14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2m.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56M16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2z"></path>
                      </svg>
                    </div>
                  </span>
                  <div>
                    <div className="feature-title">Global Networks</div>
                    <div className="feature-desc">
                      Accept and Send Payments from anywhere with a wide range
                      of payment methods
                    </div>
                  </div>
                </div>
                <div className="feature-item feature-item-last">
                  <span className="feature-icon">
                    <div className="icon-group">
                      <svg
                        fill="#fff"
                        focusable="false"
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                      >
                        <path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66s.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21"></path>
                      </svg>
                    </div>
                  </span>
                  <div>
                    <div className="feature-title">One Powerful System</div>
                    <div className="feature-desc">
                      Streamline your payments through one unified API that
                      evolves with you{" "}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* {dataUser && <Dialog
        isOpen
        onClose={() => setDataUser(null)}
        canOutsideClickClose={false}
        canEscapeKeyClose={false}
        className='verify-email-dialog'
      >
        <div className={Classes.DIALOG_BODY}>
          <div className='content'>
            <Icon icon='envelope' size={40} intent='primary' />
            <h3 className='title'>Verify your email</h3>
            <div className='description'>
              <p>We sent verification code to the email address.</p>
              <p>Please check your inbox and enter the code below.</p>
            </div>
            <FormGroup {...error && { intent: 'danger', helperText: 'This field is required.' }} className='form-input-code'>
              <InputGroup placeholder='Enter code' {...error && { intent: 'danger' }} disabled={loading} defaultValue={code} onChange={e => setCode(e.target.value)} />
            </FormGroup>
            <Button fill text='Verify Code' intent='primary' loading={loading} onClick={() => handleVerifyCode()} />
          </div>
        </div>
      </Dialog>} */}
    </div>
  );
};
